/**
 * @module @repo/global-login-sdk/request-access
 * Einbettbarer „Zugang anfragen"-Dialog + Hook — ohne Redirect. Ruft die
 * SECURITY-DEFINER-RPC `identity.request_access` für die aktuelle Session auf
 * und legt einen `pending`-Grant an (erscheint im Admin-Board unter „Offen").
 * Apps mit deaktiviertem Self-Service (`self_service=false`) melden `P0100`;
 * dann wird statt des Formulars der reine Admin-Hinweis gezeigt.
 */
import { useCallback, useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { useGlobalLogin } from './react';
import { Dialog, DialogButton, Spinner } from './dialog';
import { DialogResult } from './dialog-result';

/** Statuswerte einer Zugangs-Anfrage. */
type RequestStatus = 'idle' | 'pending' | 'success' | 'error';

/**
 * Übersetzt bekannte Postgres-`SQLSTATE`-Codes der RPC in freundliche, für
 * Endnutzer gedachte deutsche Texte. Bewusst ohne die rohe PostgREST-/Postgres-
 * Meldung, da diese interne Schema-, Funktions- und Rollennamen leaken würde
 * (MED-1). Alles Unbekannte fällt auf eine generische Meldung zurück.
 *
 * @param code Der `code`-Wert des PostgREST-Fehlers (kann `undefined` sein).
 * @returns Ein anzeigbarer, nicht-leakender Fehlertext.
 */
function friendlyRpcError(code: string | undefined): string {
  switch (code) {
    case 'P0002':
      // no_data_found → App/Slug serverseitig nicht (mehr) auflösbar.
      return 'Diese App ist momentan nicht verfügbar. Bitte versuche es später erneut.';
    case '28000':
      // invalid_authorization_specification → keine gültige Session.
      return 'Bitte melde dich zuerst an, um Zugang anzufragen.';
    default:
      return 'Die Anfrage konnte nicht gesendet werden. Bitte versuche es später erneut.';
  }
}

/** Rückgabe von {@link useRequestAccess}. */
export interface UseRequestAccessResult {
  /** Löst die Anfrage aus (optionale Freitext-Nachricht an den Admin). */
  request: (message?: string) => Promise<void>;
  /** Aktueller Fortschritt der Anfrage. */
  status: RequestStatus;
  /** Fehlermeldung bei `status === 'error'`, sonst `null`. */
  error: string | null;
  /** `true`, wenn die App laut RPC (`P0100`) keine Selbst-Anfrage erlaubt. */
  selfServiceDenied: boolean;
  /** Setzt den Hook auf den Ausgangszustand zurück. */
  reset: () => void;
}

/**
 * Hook für die Zugangs-Anfrage der konfigurierten App — ohne Redirect. Kapselt
 * den RPC-Aufruf inkl. `P0100`-Sonderfall (Self-Service deaktiviert): dann wird
 * `selfServiceDenied` gesetzt und der Status auf `error` mit klarer Meldung.
 * Andere Fehler werden über {@link friendlyRpcError} auf sichere Texte gemappt;
 * die rohe Meldung geht ausschließlich in `console.error` (Diagnose), nie in die
 * UI, um kein internes Schema zu leaken (MED-1).
 *
 * @returns Steuerobjekt, siehe {@link UseRequestAccessResult}.
 */
export function useRequestAccess(): UseRequestAccessResult {
  const { client, config } = useGlobalLogin();
  const [status, setStatus] = useState<RequestStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [selfServiceDenied, setSelfServiceDenied] = useState(false);

  const request = useCallback(
    async (message?: string): Promise<void> => {
      setStatus('pending');
      setError(null);
      setSelfServiceDenied(false);
      try {
        const trimmed = message?.trim();
        const { error: rpcError } = await client.schema('identity').rpc('request_access', {
          p_app_slug: config.appSlug,
          p_message: trimmed ? trimmed : null,
        });
        if (rpcError) {
          // Rohe PostgREST-/Postgres-Meldung NUR zur Diagnose loggen — nie in der
          // UI zeigen (leakt sonst Schema-/Funktions-/Rollennamen, MED-1).
          console.error('[global-login-sdk] request_access RPC error', rpcError);
          if (rpcError.code === 'P0100') {
            // Dedizierter Code: Self-Service explizit deaktiviert → reiner Admin-Hinweis.
            setSelfServiceDenied(true);
            setError('Diese App vergibt Zugänge nur über einen Administrator.');
          } else {
            // Alles andere (inkl. P0001 = Misskonfig „keine Standard-Rolle") generisch
            // und nicht als selfServiceDenied — keine Fehl-Klassifikation mehr.
            setError(friendlyRpcError(rpcError.code));
          }
          setStatus('error');
          return;
        }
        setStatus('success');
      } catch (err) {
        // Auch geworfene Fehler (Netzwerk etc.) generisch halten + roh nur loggen.
        console.error('[global-login-sdk] request_access failed', err);
        setError('Die Anfrage konnte nicht gesendet werden. Bitte versuche es später erneut.');
        setStatus('error');
      }
    },
    [client, config.appSlug],
  );

  const reset = useCallback((): void => {
    setStatus('idle');
    setError(null);
    setSelfServiceDenied(false);
  }, []);

  return { request, status, error, selfServiceDenied, reset };
}

/** Props von {@link RequestAccessDialog}. */
export interface RequestAccessDialogProps {
  /** Sichtbarkeit des Dialogs. */
  open: boolean;
  /** Schließen-Callback. */
  onClose: () => void;
  /** Klartext-Name der App (für Titel + Texte). */
  appLabel: string;
  /**
   * Ob die App Selbst-Anfragen erlaubt. Default `true`. Bei `false` wird direkt
   * der Admin-Hinweis statt des Formulars gezeigt (ohne einen RPC-Versuch).
   */
  selfService?: boolean;
}

/**
 * Einbettbarer „Zugang anfragen"-Dialog. Deckt alle UI-Zustände ab: Formular
 * (idle/empty), Senden (pending/loading), Bestätigung (success) und Fehler
 * (error). Ist Self-Service deaktiviert — per Prop `selfService={false}` oder
 * zur Laufzeit via `P0001` erkannt — zeigt er statt des Formulars den reinen
 * Admin-Hinweis. Der Success-Zustand ist eine `role="status"`-Live-Region und
 * holt den Fokus in den Dialog zurück (Ansage + Esc bleibt bedienbar).
 *
 * @param props Siehe {@link RequestAccessDialogProps}.
 * @returns Der gerenderte Dialog.
 */
export function RequestAccessDialog(props: RequestAccessDialogProps): ReactNode {
  const { open, onClose, appLabel, selfService = true } = props;
  const { request, status, error, selfServiceDenied, reset } = useRequestAccess();
  const [message, setMessage] = useState('');
  const messageId = useId();

  useEffect(() => {
    if (open) {
      reset();
      setMessage('');
    }
  }, [open, reset]);

  const adminOnly = selfService === false || selfServiceDenied;
  const pending = status === 'pending';

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    await request(message);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Zugang anfragen"
      description={`Du bist noch nicht für ${appLabel} freigeschaltet.`}
    >
      {adminOnly ? (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Zugang wird vom Administrator vergeben — bitte wende dich an einen Admin.
          </p>
          <div className="flex justify-end">
            <DialogButton onClick={onClose}>Verstanden</DialogButton>
          </div>
        </div>
      ) : status === 'success' ? (
        <DialogResult
          icon={
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              className="mt-0.5 size-5 shrink-0 text-emerald-600"
            >
              <path
                d="M20 6L9 17l-5-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          onClose={onClose}
        >
          Anfrage gesendet — ein Admin schaltet dich frei. Nach der Freigabe wirst du beim nächsten
          Login zugelassen.
        </DialogResult>
      ) : (
        <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4">
          {status === 'error' && error ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <label htmlFor={messageId} className="text-sm font-medium">
              Nachricht an den Admin <span className="text-muted-foreground">(optional)</span>
            </label>
            <textarea
              id={messageId}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              disabled={pending}
              rows={3}
              placeholder="Warum brauchst du Zugang?"
              className="min-h-20 w-full resize-y rounded-md border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-[var(--focus)] focus-visible:outline-none disabled:opacity-60"
            />
          </div>
          <div className="flex justify-end gap-2">
            <DialogButton variant="secondary" onClick={onClose} disabled={pending}>
              Abbrechen
            </DialogButton>
            <DialogButton type="submit" disabled={pending}>
              {pending ? (
                <>
                  <Spinner />
                  Wird gesendet …
                </>
              ) : (
                'Zugang anfragen'
              )}
            </DialogButton>
          </div>
        </form>
      )}
    </Dialog>
  );
}
